import { supabase } from "@/integrations/supabase/client";

interface Profissao {
  id: string;
  nome: string;
  descricao?: string;
  createdAt: string;
  updatedAt: string;
}

interface Colaborador {
  id: string;
  nome: string;
  profissao: string;
  telefone?: string;
  email?: string;
  endereco?: string;
  observacoes?: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

// Referência estática: principais profissões do canteiro de obra no Brasil.
const DEFAULT_PROFISSOES: Profissao[] = [
  "Pedreiro", "Servente", "Armador", "Carpinteiro", "Eletricista", "Encanador",
  "Pintor", "Azulejista", "Soldador", "Operador de Máquinas", "Gesseiro",
  "Telhadista", "Vidraceiro", "Marceneiro", "Bombeiro Hidráulico",
  "Técnico em Segurança do Trabalho", "Mestre de Obras", "Operador de Guincho",
  "Serralheiro", "Impermeabilizador",
].map((nome, i) => ({
  id: `def-prof-${i + 1}`,
  nome,
  createdAt: "2025-01-01T10:00:00.000Z",
  updatedAt: "2025-01-01T10:00:00.000Z",
}));

let _profissoes: Profissao[] = [];
let _colaboradores: Colaborador[] = [];

function rowToProfissao(row: any): Profissao {
  return { id: row.id, nome: row.nome, descricao: row.descricao ?? undefined, createdAt: row.created_at, updatedAt: row.updated_at };
}
function rowToColaborador(row: any): Colaborador {
  return {
    id: row.id, nome: row.nome, profissao: row.profissao ?? "", telefone: row.telefone ?? undefined,
    email: row.email ?? undefined, endereco: row.endereco ?? undefined, observacoes: row.observacoes ?? undefined,
    ativo: row.ativo ?? true, createdAt: row.created_at, updatedAt: row.updated_at,
  };
}
async function currentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

class WorkforceCache {
  static async hydrate(): Promise<void> {
    const [p, c] = await Promise.all([
      supabase.from("profissoes").select("*").order("created_at", { ascending: false }),
      supabase.from("colaboradores").select("*").order("created_at", { ascending: false }),
    ]);
    if (p.error) console.error("WorkforceCache.hydrate profissoes:", p.error.message);
    else _profissoes = (p.data ?? []).map(rowToProfissao);
    if (c.error) console.error("WorkforceCache.hydrate colaboradores:", c.error.message);
    else _colaboradores = (c.data ?? []).map(rowToColaborador);
  }

  // Profissões
  static getProfissoes(): Profissao[] {
    return [..._profissoes, ...DEFAULT_PROFISSOES];
  }
  static setProfissoes(profissoes: Profissao[]): void {
    _profissoes = profissoes;
  }
  static addProfissao(profissao: Profissao): void {
    const now = new Date().toISOString();
    const temp: Profissao = { ...profissao, id: profissao.id || `tmp-${Date.now()}`, createdAt: profissao.createdAt || now, updatedAt: now };
    _profissoes = [temp, ..._profissoes];
    (async () => {
      const userId = await currentUserId();
      if (!userId) return;
      const { data, error } = await supabase.from("profissoes").insert({ user_id: userId, nome: profissao.nome, descricao: profissao.descricao ?? null }).select("*").single();
      if (error) { console.error("WorkforceCache.addProfissao:", error.message); return; }
      _profissoes = _profissoes.map((x) => (x.id === temp.id ? rowToProfissao(data) : x));
    })();
  }
  static updateProfissao(updated: Profissao): void {
    _profissoes = _profissoes.map((p) => (p.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : p));
    (async () => { const { error } = await supabase.from("profissoes").update({ nome: updated.nome, descricao: updated.descricao ?? null }).eq("id", updated.id); if (error) console.error("WorkforceCache.updateProfissao:", error.message); })();
  }
  static deleteProfissao(id: string): void {
    _profissoes = _profissoes.filter((p) => p.id !== id);
    (async () => { const { error } = await supabase.from("profissoes").delete().eq("id", id); if (error) console.error("WorkforceCache.deleteProfissao:", error.message); })();
  }

  // Colaboradores
  static getColaboradores(): Colaborador[] {
    return _colaboradores;
  }
  static setColaboradores(colaboradores: Colaborador[]): void {
    _colaboradores = colaboradores;
  }
  static addColaborador(colaborador: Colaborador): void {
    const now = new Date().toISOString();
    const temp: Colaborador = { ...colaborador, id: colaborador.id || `tmp-${Date.now()}`, createdAt: colaborador.createdAt || now, updatedAt: now };
    _colaboradores = [temp, ..._colaboradores];
    (async () => {
      const userId = await currentUserId();
      if (!userId) return;
      const { data, error } = await supabase.from("colaboradores").insert({
        user_id: userId, nome: colaborador.nome, profissao: colaborador.profissao ?? null, telefone: colaborador.telefone ?? null,
        email: colaborador.email ?? null, endereco: colaborador.endereco ?? null, observacoes: colaborador.observacoes ?? null, ativo: colaborador.ativo ?? true,
      }).select("*").single();
      if (error) { console.error("WorkforceCache.addColaborador:", error.message); return; }
      _colaboradores = _colaboradores.map((x) => (x.id === temp.id ? rowToColaborador(data) : x));
    })();
  }
  static updateColaborador(updated: Colaborador): void {
    _colaboradores = _colaboradores.map((c) => (c.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : c));
    (async () => {
      const { error } = await supabase.from("colaboradores").update({
        nome: updated.nome, profissao: updated.profissao ?? null, telefone: updated.telefone ?? null,
        email: updated.email ?? null, endereco: updated.endereco ?? null, observacoes: updated.observacoes ?? null, ativo: updated.ativo ?? true,
      }).eq("id", updated.id);
      if (error) console.error("WorkforceCache.updateColaborador:", error.message);
    })();
  }
  static deleteColaborador(id: string): void {
    _colaboradores = _colaboradores.filter((c) => c.id !== id);
    (async () => { const { error } = await supabase.from("colaboradores").delete().eq("id", id); if (error) console.error("WorkforceCache.deleteColaborador:", error.message); })();
  }

  static resetToDefaults(): void {
    _profissoes = [];
    _colaboradores = [];
  }
}

export { WorkforceCache };
export type { Profissao, Colaborador };
